import { format } from 'date-fns'
import { OnlyViewHoc } from 'hocs'
import { useState } from 'react'

import { formatHours } from 'helpers/formatText'
import { useStores } from 'hooks/useStores'
import { ProcessedLogisticObject } from 'redux/services/logistic/types'

import { AppButton } from 'components/AppButton'
import { AppModalHeader } from 'components/AppModal/AppModalHeader'

import { LOGISTIC_STATUS_TEXT } from 'constants/logistic'
import { LogisticStatus } from 'models/logistic'
import { UserRole } from 'models/user'

import { DocumentsList, LabledInfo, StatusHistory } from 'shared/ui'

import { DenyLogisticModal } from './Deny'
import styles from './Details.module.scss'
import { EditLogisticModal } from './Edit'
import { RepeatLogisticModal } from './Repeat'
import { UpdateStatus } from './UpdateStatus'

interface DetailsProps {
  data: ProcessedLogisticObject
  onClose: () => void
}

export const Details = ({ data, onClose }: DetailsProps) => {
  const { userStore } = useStores()
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const role = userStore.user?.role as UserRole

  return (
    <>
      <AppModalHeader
        onClose={onClose}
        title={`№${data.id} ${data.project?.name || ''}`}
      />
      <div className={styles.container}>
        <LabledInfo label="ФИО клиента">{data.client_full_name}</LabledInfo>
        <LabledInfo label="Адрес доставки">{data.address}</LabledInfo>
        <LabledInfo label="Стоимость доставки">{data.cost}</LabledInfo>
        <LabledInfo label="Оплачено">{data.is_paid ? 'да' : 'нет'}</LabledInfo>
        <LabledInfo label="Номер">{data.phone}</LabledInfo>
        {data.author && (
          <LabledInfo label="Автор">
            {data.author?.full_name} {data.author?.phone}
          </LabledInfo>
        )}
        <LabledInfo label="Период">
          {data.start_delivery
            ? format(new Date(data.start_delivery), 'yyyy-MM-dd HH:mm')
            : '-'}
          <br />
          {formatHours(data.end_delivery)}
        </LabledInfo>
        <LabledInfo label="Статус">
          {LOGISTIC_STATUS_TEXT[data.status]}
        </LabledInfo>
        <LabledInfo label="Тип">{data.card_type}</LabledInfo>
        <LabledInfo label="Филлиал">{data.branch?.name || '-'}</LabledInfo>
        <LabledInfo label="Комментарий">
          {data.comments?.map(c => {
            return (
              <div className={styles.comment}>
                <p>{c.text}</p>
                <span>
                  {format(new Date(c.created_at), 'dd.MM.yyyy HH:mm')}
                </span>
              </div>
            )
          })}
        </LabledInfo>

        {data.anketa_file && (
          <a href={data.anketa_file} target="_blank" rel="noreferrer">
            <AppButton variant="gray" size="small">
              Анкета
            </AppButton>
          </a>
        )}

        <StatusHistory list={data.status_list} />

        <DocumentsList list={data.documents} editable />

        <OnlyViewHoc>
          {data ? (
            <>
              {UserRole.Executor === role && (
                <>
                  {[
                    LogisticStatus.waiting,
                    LogisticStatus.repeat,
                    LogisticStatus.for_delivery,
                  ].includes(data.status) && (
                    <>
                      {/* <SetDeliveryman data={data} id={data.id} /> */}
                      <UpdateStatus
                        key={data.id}
                        id={data.id}
                        status={LogisticStatus.waiting}
                      />
                    </>
                  )}

                  {[LogisticStatus.denied].includes(data.status) && (
                    <>
                      <RepeatLogisticModal key={data.id} data={data} />
                      <DenyLogisticModal id={data.id} />
                    </>
                  )}

                  {data.status === LogisticStatus.given && (
                    <>
                      <UpdateStatus
                        key={`${data.id}-accepted`}
                        id={data.id}
                        status={LogisticStatus.accepted}
                      />
                      <UpdateStatus
                        key={`${data.id}-no-accepted`}
                        id={data.id}
                        status={LogisticStatus.no_accepted}
                      />
                    </>
                  )}
                </>
              )}

              {UserRole.ControllerUKC === role ? (
                <>
                  {data.status === LogisticStatus.given && (
                    <>
                      <UpdateStatus
                        id={data.id}
                        status={LogisticStatus.accepted}
                      />
                      <UpdateStatus
                        id={data.id}
                        status={LogisticStatus.no_accepted}
                      />
                    </>
                  )}
                </>
              ) : null}

              {[UserRole.Author, UserRole.ControllerUKC].includes(role) ? (
                <>
                  {[LogisticStatus.waiting, LogisticStatus.in_process].includes(
                    data.status,
                  ) && (
                    <AppButton onClick={() => setIsEditModalOpen(true)}>
                      Редактировать
                    </AppButton>
                  )}
                </>
              ) : null}
            </>
          ) : null}
        </OnlyViewHoc>
      </div>

      {isEditModalOpen && (
        <EditLogisticModal
          data={data}
          onClose={() => setIsEditModalOpen(false)}
        />
      )}
    </>
  )
}
